import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEvent } from '../contexts/EventContext';
import apiService from '../services/api.service';
import { API_BASE_URL } from '../config/api';
import TipTapEditor from '../components/TipTapEditor';
import { toast } from 'sonner';
import {
  Send,
  Mail,
  Eye,
  Loader2,
  Users,
  ChevronDown,
  ChevronUp,
  Clock,
  X,
  Download,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Separator } from '../components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  RadioGroup,
  RadioGroupItem,
} from '../components/ui/radio-group';

// Mini error boundary for TipTapEditor — prevents editor crash from taking down the whole page
class EditorErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  componentDidCatch(error: Error) {
    console.error('[TipTapEditor] Crash:', error);
  }
  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="border border-destructive/50 rounded-md p-4 text-sm text-destructive">
          Erreur lors du chargement de l&apos;éditeur. Rechargez la page.
          <pre className="mt-2 text-xs opacity-60">{this.state.error?.message}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

interface Category {
  id: string;
  name: string;
}

interface Team {
  id: string;
  team_name: string;
  category_name: string;
  category_id: string;
  participants?: any[];
}

interface Attachment {
  id: string;
  name: string;
  size_bytes: number;
}

// Limite du serveur (`PIECES_JOINTES_MAX_OCTETS`) : rappelée ici pour le dire
// avant l'envoi plutôt que de se le faire refuser.
const PIECES_JOINTES_MAX_OCTETS = 10 * 1024 * 1024;
const TYPES_PIECES_JOINTES = '.pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv';
// Les images sont servies par l'API : l'éditeur les affiche depuis son origine.
const API_ORIGIN = API_BASE_URL.replace(/\/api\/v1\/?$/, '');

interface Message {
  attachments?: Attachment[];
  id: string;
  subject: string;
  recipient_filter: string;
  recipient_count: number;
  failed_count: number;
  status: string;
  sent_at: string | null;
  sent_by_name: string;
  html_content: string;
}

const Messages: React.FC = () => {
  const { t } = useTranslation();
  const { selectedEventId, selectedEvent } = useEvent();

  // Compose state
  const [subject, setSubject] = useState('');
  const [htmlContent, setHtmlContent] = useState('<p></p>');
  const [recipientType, setRecipientType] = useState<'all' | 'categories' | 'teams' | 'volunteers' | 'everyone'>('all');
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);

  // Arrivée depuis la sélection de l'écran Équipes (« Envoyer un message ») :
  // `?teams=<uuid>,<uuid>` pré-coche ces équipes. Lu une fois, puis retiré de
  // l'URL pour qu'un rechargement ne ré-impose pas une sélection périmée.
  // Même chose depuis la liste des Bénévoles : `?volunteers=<uuid>,…` cible
  // ceux qu'on a cochés ; vide, « aux bénévoles » s'adresse à tous.
  const [selectedVolunteerIds, setSelectedVolunteerIds] = useState<string[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const fromTeams = searchParams.get('teams');
    const fromVolunteers = searchParams.get('volunteers');
    if (!fromTeams && !fromVolunteers) return;
    const teamIds = (fromTeams || '').split(',').filter(Boolean);
    const volunteerIds = (fromVolunteers || '').split(',').filter(Boolean);
    if (teamIds.length) {
      setRecipientType('teams');
      setSelectedTeamIds(teamIds);
    } else if (volunteerIds.length) {
      setRecipientType('volunteers');
      setSelectedVolunteerIds(volunteerIds);
    }
    searchParams.delete('teams');
    searchParams.delete('volunteers');
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Data
  const [categories, setCategories] = useState<Category[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showDetail, setShowDetail] = useState<Message | null>(null);
  const [historyOpen, setHistoryOpen] = useState(true);

  // Pièces jointes déjà téléversées pour le message en cours d'écriture.
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [attachmentsUploading, setAttachmentsUploading] = useState(0);
  const [dropActive, setDropActive] = useState(false);
  const attachmentInput = React.useRef<HTMLInputElement>(null);
  const attachmentsTotal = attachments.reduce((n, a) => n + a.size_bytes, 0);
  const attachmentsTooHeavy = attachmentsTotal > PIECES_JOINTES_MAX_OCTETS;

  // Un autre événement : les fichiers déposés appartiennent à l'ancien.
  useEffect(() => {
    setAttachments([]);
  }, [selectedEventId]);

  const formatSize = (bytes: number) => bytes < 1024 * 1024
    ? t('messages.sizeKb', { size: Math.max(1, Math.round(bytes / 1024)) })
    : t('messages.sizeMb', { size: (bytes / 1024 / 1024).toLocaleString(undefined, { maximumFractionDigits: 1 }) });

  const uploadImage = async (file: File): Promise<string | null> => {
    if (!selectedEventId) return null;
    try {
      const res = await apiService.uploadEventMessageFile(selectedEventId, file, 'image');
      return `${API_ORIGIN}${res.url}`;
    } catch (err: any) {
      toast.error(err.response?.data?.detail || t('messages.uploadFailed'));
      return null;
    }
  };

  const addAttachments = async (files: File[]) => {
    if (!selectedEventId) return;
    for (const file of files) {
      setAttachmentsUploading(n => n + 1);
      try {
        const res = await apiService.uploadEventMessageFile(selectedEventId, file, 'attachment');
        setAttachments(prev => [...prev, { id: res.id, name: res.name, size_bytes: res.size_bytes }]);
      } catch (err: any) {
        toast.error(`${file.name} : ${err.response?.data?.detail || t('messages.uploadFailed')}`);
      } finally {
        setAttachmentsUploading(n => n - 1);
      }
    }
  };

  const removeAttachment = async (attachment: Attachment) => {
    if (!selectedEventId) return;
    setAttachments(prev => prev.filter(a => a.id !== attachment.id));
    try {
      await apiService.deleteEventMessageFile(selectedEventId, attachment.id);
    } catch (err: any) {
      // Retirée de l'écran quoi qu'il arrive : elle ne partira pas, puisque
      // l'envoi ne joint que ce qui est listé ici.
      console.error('[Messages] Pièce jointe non effacée côté serveur:', err);
    }
  };

  const downloadAttachment = async (attachment: Attachment) => {
    if (!selectedEventId) return;
    try {
      const blob = await apiService.downloadEventMessageFile(selectedEventId, attachment.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || t('common.error'));
    }
  };

  // Load categories, teams, and message history
  useEffect(() => {
    if (selectedEventId) {
      loadData();
    }
  }, [selectedEventId]);

  const loadData = async () => {
    if (!selectedEventId) return;
    setLoading(true);
    setError('');
    try {
      // Load categories
      try {
        const catRes = await apiService.getCategories(selectedEventId);
        const catData = catRes?.data?.categories || catRes?.data || [];
        setCategories(Array.isArray(catData) ? catData : []);
      } catch (e) {
        console.error('[Messages] Failed to load categories:', e);
        setCategories([]);
      }

      // Load teams
      try {
        const teamRes = await apiService.getTeams(selectedEventId);
        const teamData = teamRes?.data?.teams || teamRes?.data || [];
        setTeams(Array.isArray(teamData) ? teamData : []);
      } catch (e) {
        console.error('[Messages] Failed to load teams:', e);
        setTeams([]);
      }

      // Load message history
      try {
        const msgRes = await apiService.getEventMessages(selectedEventId);
        const msgData = msgRes?.messages || [];
        setMessages(Array.isArray(msgData) ? msgData : []);
      } catch (e) {
        console.error('[Messages] Failed to load messages:', e);
        setMessages([]);
      }
    } catch (err: any) {
      console.error('[Messages] Unexpected error in loadData:', err);
      setError(err.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  /** Relit l'historique seul : sert au suivi d'un envoi en cours. */
  const reloadHistory = async () => {
    if (!selectedEventId) return;
    try {
      const msgRes = await apiService.getEventMessages(selectedEventId);
      const msgData = msgRes?.messages || [];
      setMessages(Array.isArray(msgData) ? msgData : []);
    } catch (e) {
      console.error('[Messages] Failed to reload messages:', e);
    }
  };

  // Les emails partent APRÈS la réponse du serveur : tant qu'un message est
  // « en cours », on relit l'historique pour afficher la fin de l'envoi.
  const envoiEnCours = messages.some(m => m.status === 'sending');
  useEffect(() => {
    if (!envoiEnCours) return;
    const timer = setInterval(() => { void reloadHistory(); }, 5000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envoiEnCours, selectedEventId]);

  // Périmètre courant, sous la forme attendue par l'API.
  const recipientFilter = useMemo(() => {
    if (recipientType === 'categories') {
      return { type: 'categories', category_ids: selectedCategoryIds };
    }
    if (recipientType === 'teams') {
      return { type: 'teams', team_ids: selectedTeamIds };
    }
    if (recipientType === 'volunteers') {
      return selectedVolunteerIds.length ? { type: 'volunteers', volunteer_ids: selectedVolunteerIds } : { type: 'volunteers' };
    }
    if (recipientType === 'everyone') return { type: 'everyone' };
    return { type: 'all' };
  }, [recipientType, selectedCategoryIds, selectedTeamIds, selectedVolunteerIds]);

  /**
   * Décompte des destinataires : c'est le SERVEUR qui répond, pour tous les
   * périmètres.
   *
   * Il se calculait ici, à partir des équipes chargées par la page. Deux
   * défauts : les bénévoles n'en font pas partie (ils ne sont pas des équipes),
   * d'où le « calculé à l'envoi » ; et faute de participants chargés, le
   * compteur retombait sur le nombre d'ÉQUIPES — une autre unité, affichée
   * comme un nombre d'emails.
   *
   * L'endpoint appelle la fonction qui servira à l'envoi : l'annonce ne peut
   * plus diverger du résultat. `null` = pas encore connu, à distinguer de zéro.
   */
  const [recipientCount, setRecipientCount] = useState<number | null>(null);

  useEffect(() => {
    if (!selectedEventId) {
      setRecipientCount(null);
      return;
    }

    // Un périmètre vide ne vaut pas un aller-retour : la réponse est zéro.
    if (
      (recipientFilter.type === 'categories' && !selectedCategoryIds.length) ||
      (recipientFilter.type === 'teams' && !selectedTeamIds.length)
    ) {
      setRecipientCount(0);
      return;
    }

    const controller = new AbortController();
    // Cocher cinq catégories d'affilée ne doit pas déclencher cinq requêtes ;
    // et l'abort garantit qu'une réponse lente ne vienne pas écraser un
    // décompte plus récent.
    const timer = setTimeout(async () => {
      setRecipientCount(null);
      try {
        const { recipient_count } = await apiService.previewEventMessageRecipients(
          selectedEventId, recipientFilter, controller.signal
        );
        setRecipientCount(recipient_count);
      } catch (err: any) {
        if (controller.signal.aborted || err?.code === 'ERR_CANCELED') return;
        console.error('[Messages] Décompte des destinataires indisponible:', err);
        setRecipientCount(0);
      }
    }, 300);

    return () => { clearTimeout(timer); controller.abort(); };
  }, [selectedEventId, recipientFilter, selectedCategoryIds.length, selectedTeamIds.length]);

  const buildRecipientFilter = () => recipientFilter;

  // Tant que le décompte n'est pas revenu, on ne laisse pas partir : le nombre
  // annoncé dans la confirmation doit être celui qui sera envoyé.
  const canSend = !!subject.trim() && htmlContent !== '<p></p>' && !!htmlContent.trim()
    && recipientCount !== null && recipientCount > 0
    && attachmentsUploading === 0 && !attachmentsTooHeavy;

  const handleSend = async () => {
    if (!selectedEventId || !canSend) return;
    setSending(true);
    setShowConfirm(false);
    try {
      const result = await apiService.sendEventMessage(selectedEventId, {
        subject,
        html_content: htmlContent,
        recipient_filter: buildRecipientFilter(),
        attachment_ids: attachments.map(a => a.id),
      });
      // Le serveur a enregistré le message ; les emails partent maintenant.
      // Le résultat final s'affiche dans l'historique (suivi ci-dessus).
      toast.success(t('messages.sendStarted', { count: result.recipient_count }));
      // Reset form
      setSubject('');
      setHtmlContent('<p></p>');
      setRecipientType('all');
      setSelectedCategoryIds([]);
      setSelectedTeamIds([]);
      setAttachments([]);
      // Reload history
      await loadData();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setSending(false);
    }
  };

  const toggleCategory = (categoryId: string) => {
    setSelectedCategoryIds(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const toggleTeam = (teamId: string) => {
    setSelectedTeamIds(prev =>
      prev.includes(teamId)
        ? prev.filter(id => id !== teamId)
        : [...prev, teamId]
    );
  };

  // Statut d'envoi : une pastille de 7 px + libellé (charte — plus d'aplat saturé).
  const statusDot = (dotClass: string, label: string) => (
    <span className="inline-flex items-center gap-2 text-xs font-medium text-foreground">
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotClass}`} />
      {label}
    </span>
  );

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'sent':
        return statusDot('bg-success', t('messages.statusSent'));
      case 'sending':
        return statusDot('bg-info', t('messages.statusSending'));
      case 'partial_failure':
        return statusDot('bg-warning', t('messages.statusPartial'));
      case 'failed':
        return statusDot('bg-destructive', t('messages.statusFailed'));
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatRecipientFilter = (filterJson: string) => {
    try {
      const filter = typeof filterJson === 'string' ? JSON.parse(filterJson) : filterJson;
      if (filter.type === 'all') return t('messages.recipientsAll');
      if (filter.type === 'categories') return `${t('messages.recipientsByCategory')} (${filter.category_ids?.length || 0})`;
      if (filter.type === 'teams') return `${t('messages.recipientsByTeam')} (${filter.team_ids?.length || 0})`;
      if (filter.type === 'mixed') return t('messages.recipientsMixed');
      return filter.type;
    } catch {
      return '-';
    }
  };

  if (!selectedEventId) {
    return (
      <div className="p-4 sm:p-6 text-center text-muted-foreground mt-16">
        <Mail size={48} className="mx-auto mb-4 opacity-40" />
        <p>{t('messages.selectEventFirst')}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-4 sm:p-6 flex items-center justify-center mt-16">
        <Loader2 className="animate-spin mr-2" />
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-4xl mx-auto">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* ====== COMPOSE SECTION ====== */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle>
            {t('messages.compose')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Subject */}
          <div>
            <Label htmlFor="subject">{t('messages.subject')}</Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={t('messages.subjectPlaceholder')}
              className="mt-1"
            />
          </div>

          {/* Recipients */}
          <div>
            <Label>{t('messages.recipients')}</Label>
            <div className="mt-2 space-y-3">
              <RadioGroup
                value={recipientType}
                onValueChange={(v) => setRecipientType(v as 'all' | 'categories' | 'teams' | 'volunteers' | 'everyone')}
                className="flex flex-wrap gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="all" id="r-all" />
                  <Label htmlFor="r-all" className="cursor-pointer font-normal">
                    {t('messages.recipientsAll')}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="categories" id="r-categories" />
                  <Label htmlFor="r-categories" className="cursor-pointer font-normal">
                    {t('messages.recipientsByCategory')}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="teams" id="r-teams" />
                  <Label htmlFor="r-teams" className="cursor-pointer font-normal">
                    {t('messages.recipientsByTeam')}
                  </Label>
                </div>
                {/* Les bénévoles vivent hors du flux d'inscription : ce sont
                    des destinataires à part, pas un filtre sur les équipes. */}
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="volunteers" id="r-volunteers" />
                  <Label htmlFor="r-volunteers" className="cursor-pointer font-normal">
                    {t('messages.recipientsVolunteers')}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="everyone" id="r-everyone" />
                  <Label htmlFor="r-everyone" className="cursor-pointer font-normal">
                    {t('messages.recipientsEveryone')}
                  </Label>
                </div>
              </RadioGroup>

              {/* Le dédoublonnage ne concerne que « tout le monde » : seul ce
                  périmètre peut croiser la même personne des deux côtés. */}
              {recipientType === 'everyone' && (
                <p className="text-xs text-muted-foreground pl-1">
                  {t('messages.recipientsDedup')}
                </p>
              )}

              {recipientType === 'volunteers' && selectedVolunteerIds.length > 0 && (
                <p className="flex flex-wrap items-center gap-2 pl-1 text-xs text-muted-foreground">
                  {t('messages.volunteersSelected', { count: selectedVolunteerIds.length })}
                  <button type="button" className="underline hover:text-foreground" onClick={() => setSelectedVolunteerIds([])}>
                    {t('messages.volunteersSelectedClear')}
                  </button>
                </p>
              )}

              {/* Category checkboxes */}
              {recipientType === 'categories' && (
                <div className="flex flex-wrap gap-2 pl-1">
                  {categories.map(cat => (
                    <label
                      key={cat.id}
                      className="flex items-center gap-1.5 text-sm cursor-pointer"
                    >
                      <Checkbox
                        checked={selectedCategoryIds.includes(cat.id)}
                        onCheckedChange={() => toggleCategory(cat.id)}
                      />
                      {cat.name}
                    </label>
                  ))}
                  {categories.length === 0 && (
                    <span className="text-sm text-muted-foreground">{t('common.noResults')}</span>
                  )}
                </div>
              )}

              {/* Team checkboxes */}
              {recipientType === 'teams' && (
                <div className="max-h-48 overflow-y-auto space-y-1 pl-1">
                  {teams.map(team => (
                    <label
                      key={team.id}
                      className="flex items-center gap-1.5 text-sm cursor-pointer"
                    >
                      <Checkbox
                        checked={selectedTeamIds.includes(team.id)}
                        onCheckedChange={() => toggleTeam(team.id)}
                      />
                      <span>{team.team_name}</span>
                      <span className="text-muted-foreground text-xs">({team.category_name})</span>
                    </label>
                  ))}
                  {teams.length === 0 && (
                    <span className="text-sm text-muted-foreground">{t('common.noResults')}</span>
                  )}
                </div>
              )}

              {/* Recipient count badge */}
              <div className="flex items-center gap-2">
                <Users size={14} className="text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {recipientCount === null
                    ? t('messages.recipientCountLoading')
                    : t('messages.recipientCount', { count: recipientCount })}
                </span>
              </div>
            </div>
          </div>

          {/* WYSIWYG Editor */}
          <div>
            <Label>{t('messages.content')}</Label>
            <div className="mt-1">
              <EditorErrorBoundary>
                <TipTapEditor
                  content={htmlContent}
                  onChange={setHtmlContent}
                  onUploadImage={uploadImage}
                />
              </EditorErrorBoundary>
            </div>
            <p className="mt-1.5 text-xs text-muted-foreground">{t('messages.contentImageHint')}</p>
          </div>

          {/* Pièces jointes */}
          <div>
            <Label>{t('messages.attachments')}</Label>
            <div
              role="button"
              tabIndex={0}
              onClick={() => attachmentInput.current?.click()}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); attachmentInput.current?.click(); } }}
              onDragOver={(e) => { e.preventDefault(); setDropActive(true); }}
              onDragLeave={() => setDropActive(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDropActive(false);
                void addAttachments(Array.from(e.dataTransfer.files));
              }}
              className={`mt-1 cursor-pointer rounded-[3px] border border-dashed px-4 py-4 text-center text-sm text-muted-foreground transition-colors ${
                dropActive ? 'border-white/60 bg-white/[0.04] text-foreground' : 'border-border hover:border-white/30'
              }`}
            >
              {attachmentsUploading > 0
                ? <span className="inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" />{t('messages.attachmentsUploading')}</span>
                : t('messages.attachmentsHint')}
            </div>
            <input
              ref={attachmentInput}
              type="file"
              multiple
              accept={TYPES_PIECES_JOINTES}
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                e.target.value = '';
                void addAttachments(files);
              }}
            />
            {attachments.length > 0 && (
              <div className="mt-2">
                <ul className="divide-y divide-border rounded-[3px] border border-border">
                  {attachments.map(a => (
                    <li key={a.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                      <span className="min-w-0 flex-1 truncate">{a.name}</span>
                      <span className="shrink-0 text-xs text-muted-foreground">{formatSize(a.size_bytes)}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 shrink-0 p-0"
                        title={t('messages.attachmentRemove')}
                        aria-label={`${t('messages.attachmentRemove')} ${a.name}`}
                        onClick={() => void removeAttachment(a)}
                      >
                        <X size={14} />
                      </Button>
                    </li>
                  ))}
                </ul>
                <p className={`mt-1.5 text-xs ${attachmentsTooHeavy ? 'text-destructive' : 'text-muted-foreground'}`}>
                  {attachmentsTooHeavy
                    ? t('messages.attachmentsTooHeavy')
                    : t('messages.attachmentsTotal', { size: formatSize(attachmentsTotal) })}
                </p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setShowPreview(true)}
              disabled={!htmlContent || htmlContent === '<p></p>'}
            >
              <Eye size={16} className="mr-2" />
              {t('messages.preview')}
            </Button>
            <Button
              onClick={() => setShowConfirm(true)}
              disabled={!canSend || sending}
            >
              {sending ? (
                <Loader2 size={16} className="mr-2 animate-spin" />
              ) : (
                <Send size={16} className="mr-2" />
              )}
              {t('messages.send')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ====== HISTORY SECTION ====== */}
      <Card>
        <CardHeader className="pb-2 cursor-pointer" onClick={() => setHistoryOpen(!historyOpen)}>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              {t('messages.history')}
              {messages.length > 0 && (
                <Badge variant="secondary" className="ml-1">{messages.length}</Badge>
              )}
            </span>
            {historyOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </CardTitle>
        </CardHeader>
        {historyOpen && (
          <CardContent>
            {messages.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                {t('messages.noHistory')}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('common.date')}</TableHead>
                      <TableHead>{t('messages.subject')}</TableHead>
                      <TableHead className="hidden sm:table-cell">{t('messages.recipients')}</TableHead>
                      <TableHead>{t('common.status')}</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {messages.map(msg => (
                      <TableRow key={msg.id}>
                        <TableCell className="whitespace-nowrap text-sm">
                          {msg.sent_at
                            ? new Date(msg.sent_at).toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : '-'}
                        </TableCell>
                        <TableCell className="font-medium max-w-[200px] truncate">
                          {msg.subject}
                          {!!msg.attachments?.length && (
                            <span className="ml-2 text-xs font-normal text-muted-foreground">
                              {t('messages.attachmentsCount', { count: msg.attachments.length })}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <span className="text-sm">
                            {msg.recipient_count}
                            {msg.failed_count > 0 && (
                              <span className="text-destructive ml-1">
                                ({msg.failed_count} {t('messages.failed')})
                              </span>
                            )}
                          </span>
                        </TableCell>
                        <TableCell>{getStatusBadge(msg.status)}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowDetail(msg)}
                          >
                            <Eye size={14} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* ====== PREVIEW DIALOG ====== */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('messages.preview')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-muted-foreground">{t('messages.subject')}</Label>
              <p className="font-medium">{subject || '-'} — {selectedEvent?.name}</p>
            </div>
            <Separator />
            <div
              className="prose prose-invert prose-sm max-w-none bg-[#111111] p-6 rounded-lg border border-border"
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
            {attachments.length > 0 && (
              <div>
                <Label className="text-muted-foreground">{t('messages.attachments')}</Label>
                <ul className="mt-1 space-y-0.5 text-sm">
                  {attachments.map(a => (
                    <li key={a.id}>{a.name} <span className="text-xs text-muted-foreground">· {formatSize(a.size_bytes)}</span></li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              {t('common.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ====== CONFIRM SEND DIALOG ====== */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('messages.confirmSendTitle')}</DialogTitle>
            <DialogDescription>
              {/* `canSend` exige un décompte résolu : ici c'est toujours un nombre. */}
              {t('messages.confirmSendDesc', { count: recipientCount ?? 0, subject })}
              {attachments.length > 0 && (
                <span className="mt-2 block">
                  {t('messages.confirmAttachments', {
                    count: attachments.length,
                    names: attachments.map(a => a.name).join(', '),
                  })}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSend} disabled={sending}>
              {sending ? (
                <Loader2 size={16} className="mr-2 animate-spin" />
              ) : (
                <Send size={16} className="mr-2" />
              )}
              {t('messages.confirmSend')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ====== MESSAGE DETAIL DIALOG ====== */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{showDetail?.subject}</DialogTitle>
          </DialogHeader>
          {showDetail && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                <span>
                  {showDetail.sent_at
                    ? new Date(showDetail.sent_at).toLocaleString('fr-FR')
                    : '-'}
                </span>
                <span>{showDetail.sent_by_name}</span>
                <span>
                  {showDetail.recipient_count} {t('messages.recipientsLabel')}
                  {showDetail.failed_count > 0 && (
                    <span className="text-destructive ml-1">
                      ({showDetail.failed_count} {t('messages.failed')})
                    </span>
                  )}
                </span>
                {getStatusBadge(showDetail.status)}
              </div>
              <Separator />
              <div
                className="prose prose-invert prose-sm max-w-none bg-[#111111] p-6 rounded-lg border border-border"
                dangerouslySetInnerHTML={{ __html: showDetail.html_content }}
              />
              {!!showDetail.attachments?.length && (
                <div>
                  <Label className="text-muted-foreground">{t('messages.attachments')}</Label>
                  <ul className="mt-1 divide-y divide-border rounded-[3px] border border-border">
                    {showDetail.attachments.map(a => (
                      <li key={a.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                        <span className="min-w-0 flex-1 truncate">{a.name}</span>
                        <span className="shrink-0 text-xs text-muted-foreground">{formatSize(a.size_bytes)}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 shrink-0 p-0"
                          title={t('messages.attachmentDownload')}
                          aria-label={`${t('messages.attachmentDownload')} ${a.name}`}
                          onClick={() => void downloadAttachment(a)}
                        >
                          <Download size={14} />
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetail(null)}>
              {t('common.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Messages;
